import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space } from '../styles/theme.js'
import { similarity as simApi } from '../lib/api.js'

// --- Styles ---

const panel = css`
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background: ${colors.bgCard};
  overflow: hidden;
`

const panelHeader = css`
  padding: ${space.sm} ${space.md};
  background: ${colors.bgSidebar};
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  color: ${colors.textSecondary};
  border-bottom: 1px solid ${colors.border};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const itemRow = css`
  display: flex;
  align-items: center;
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  cursor: pointer;
  font-size: ${font.size.sm};
  transition: background 0.1s ease;
  &:last-child { border-bottom: none; }
  &:hover { background: ${colors.bg}; }
`

const itemInfo = css`
  flex: 1;
  min-width: 0;
`

const itemPreview = css`
  font-weight: ${font.weight.medium};
  color: ${colors.text};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const itemMeta = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
`

const simScore = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  white-space: nowrap;
  margin-left: ${space.sm};
`

const simBar = css`
  width: 40px;
  height: 4px;
  background: ${colors.border};
  border-radius: 2px;
  margin-left: ${space.sm};
  overflow: hidden;
`

const simBarFill = css`
  height: 100%;
  background: ${colors.teal};
  border-radius: 2px;
`

const emptyMsg = css`
  padding: ${space.md};
  text-align: center;
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
`

// --- Component ---

// Shows similar Q&A pairs for a selected message.
// Props: projectId, messageId (null = no message selected)
export function SimilarPairsPanel() {
  let items = []
  let loading = false
  let loadedMessageId = null
  let collapsed = false
  let error = null

  async function load(projectId, messageId) {
    if (!messageId) return
    loading = true
    error = null
    loadedMessageId = messageId
    m.redraw()
    try {
      items = await simApi.findSimilarPairs(projectId, messageId, 10)
    } catch (err) {
      error = err.message
      items = []
    } finally {
      loading = false
      m.redraw()
    }
  }

  return {
    view(vnode) {
      const { projectId, messageId } = vnode.attrs

      // Reload when selected message changes
      if (messageId && messageId !== loadedMessageId && !loading) {
        load(projectId, messageId)
      }

      const title = messageId
        ? `Similar Q&A${items.length > 0 ? ` (${items.length})` : ''}`
        : 'Similar Q&A'

      const children = [
        m('div', {
          key: 'header',
          class: panelHeader,
          onclick: () => { collapsed = !collapsed; m.redraw() },
        }, [
          m('span', title),
          m('span', collapsed ? '+' : '-'),
        ]),
      ]
      if (!collapsed) {
        if (!messageId) {
          children.push(m('div', { key: 'empty', class: emptyMsg }, 'Click a message to find similar Q&A pairs'))
        } else if (loading) {
          children.push(m('div', { key: 'loading', class: emptyMsg }, 'Finding similar pairs...'))
        } else if (error) {
          children.push(m('div', { key: 'error', class: emptyMsg }, 'Pair embeddings not available'))
        } else if (items.length === 0) {
          children.push(m('div', { key: 'none', class: emptyMsg }, 'No similar pairs found'))
        } else {
          for (const item of items.slice(0, 10)) {
            children.push(m('div', {
              key: item.pairId,
              class: itemRow,
              onclick: () => m.route.set(`/projects/${projectId}/sessions/${item.sessionId}`),
            }, [
              m('div', { class: itemInfo }, [
                m('div', { class: itemPreview },
                  item.userPreview || '(no preview)'),
                m('div', { class: itemMeta },
                  item.externalId || item.sessionId.slice(0, 8)),
              ]),
              m('div', { class: simScore },
                `${Math.round(item.similarity * 100)}%`),
              m('div', { class: simBar },
                m('div', {
                  class: simBarFill,
                  style: `width: ${item.similarity * 100}%`,
                })
              ),
            ]))
          }
        }
      }

      return m('div', { class: panel }, children)
    },
  }
}
